import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { format } from "date-fns";
import {
  ArrowLeft,
  ClipboardCheck,
  ExternalLink,
  MapPin,
  StickyNote,
} from "lucide-react";
import { Main } from "@/app/layout/Main";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import type { MyQcRecordDetailDTO } from "../api/client";

function statusBadge(status: "pass" | "fail" | "pending") {
  if (status === "pass")
    return (
      <Badge variant="secondary" className="bg-emerald-600/15 text-emerald-800">
        ຜ່ານ
      </Badge>
    );
  if (status === "fail") return <Badge variant="destructive">ບໍ່ຜ່ານ</Badge>;
  return <Badge variant="outline">ລໍຖ້າ</Badge>;
}

function itemStatusBadge(status: "pass" | "fail") {
  if (status === "pass")
    return (
      <Badge variant="secondary" className="bg-emerald-600/15 text-emerald-800">
        ຜ່ານ
      </Badge>
    );
  return <Badge variant="destructive">ບໍ່ຜ່ານ</Badge>;
}

type RecordHeader = MyQcRecordDetailDTO["record"] & {
  inspectorName?: string | null;
  inspectedBy?: string;
};

export function QcRecordDetailView({
  record,
  items,
  title,
  onBack,
  showInspector,
}: {
  record: RecordHeader;
  items: MyQcRecordDetailDTO["items"];
  title: string;
  onBack: () => void;
  /** ສະແດງຜູ້ກວດ (ບັນທຶກພາຍໃນຕາມຕຳແໜ່ງ) */
  showInspector?: boolean;
}) {
  return (
    <Main className="flex flex-col gap-6 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-8 w-8 text-primary" />
          <h1 className="font-semibold text-2xl">{title}</h1>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" />
          ກັບ
        </Button>
      </div>

      <Card className="border-border py-0">
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ມາດຕະຖານ
              </p>
              <p className="font-semibold text-lg">{record.standardName}</p>
            </div>
            {statusBadge(record.overallStatus)}
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            {showInspector ? (
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">ຜູ້ກວດ</dt>
                <dd className="font-medium">
                  {record.inspectorName?.trim() || record.inspectedBy || "—"}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="text-muted-foreground">ວັນທີກວດ</dt>
              <dd className="font-medium">
                {format(new Date(record.inspectedAt), "yyyy-MM-dd HH:mm")}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ປະເພດງານ</dt>
              <dd className="font-medium">
                {record.workItemTypeName?.trim() || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ລະຫັດງານ</dt>
              <dd className="font-medium font-mono text-xs">
                {record.workItemId?.trim() || "—"}
              </dd>
            </div>
            {record.location?.trim() ? (
              <div className="sm:col-span-2">
                <dt className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="size-3.5" aria-hidden />
                  ສະຖານທີ່ກວດ
                </dt>
                <dd className="mt-0.5 font-medium">{record.location}</dd>
              </div>
            ) : null}
            {record.notes?.trim() ? (
              <div className="sm:col-span-2">
                <dt className="flex items-center gap-1.5 text-muted-foreground">
                  <StickyNote className="size-3.5" aria-hidden />
                  ຫມາຍເຫດ
                </dt>
                <dd className="mt-0.5 whitespace-pre-wrap leading-relaxed">
                  {record.notes}
                </dd>
              </div>
            ) : null}
          </dl>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="font-medium text-lg">ລາຍການກວດ</h2>
        <div className="flex flex-col gap-3">
          {items.map((it, idx) => (
            <Card
              key={it.id}
              className="overflow-hidden border-border/80 py-0 shadow-sm"
            >
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 gap-3">
                    <span
                      className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted font-semibold text-muted-foreground text-xs tabular-nums"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold leading-snug">
                        {it.itemTitle}
                      </p>
                      {it.itemDescription?.trim() ? (
                        <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
                          {it.itemDescription}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {itemStatusBadge(it.status)}
                </div>
                {it.status === "fail" &&
                (it.notes?.trim() || it.photoUrl?.trim()) ? (
                  <div className="space-y-2 rounded-lg border border-destructive/15 bg-destructive/5 p-3 text-sm">
                    {it.notes?.trim() ? (
                      <div>
                        <p className="text-muted-foreground text-xs">ເຫດຜົນ</p>
                        <p className="mt-0.5 whitespace-pre-wrap">{it.notes}</p>
                      </div>
                    ) : null}
                    {it.photoUrl?.trim() ? (
                      <div className="space-y-2">
                        <p className="text-muted-foreground text-xs">ຮູບອ້າງອີງ</p>
                        <div className="relative max-h-48 max-w-md overflow-hidden rounded-lg border bg-muted/20">
                          <AppImage
                            src={it.photoUrl}
                            alt=""
                            className="max-h-48 w-full"
                            fit="contain"
                          />
                        </div>
                        {resolveImageSrc(it.photoUrl) ? (
                          <a
                            href={resolveImageSrc(it.photoUrl) ?? undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-medium text-primary text-sm underline-offset-4 hover:underline"
                          >
                            ເປີດຮູບເຕັມ
                            <ExternalLink className="size-3.5 shrink-0" />
                          </a>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </Main>
  );
}
